'use client';

import { PropsWithChildren } from 'react';

import { useAuth } from '@/contexts/auth';
import { PageContainer } from '@/components/page-container';
import { BookingPageUnavailable } from '@/components/booking-page-unavailable';
import { useSiteFailure } from '@/hooks/use-site-failure';
import useTenant from '@/state/tenant';

// Slow on purpose: a suspension being lifted is not urgent, and this only ever
// runs in a tab that is already showing an outage.
const REPROBE_MS = 30_000;

// Sits inside the site <main> rather than around it, so the page background is
// painted from the first frame instead of after the auth requests land.
export const SiteAuthGate = ({
  children,
  renderWhileLoading,
}: PropsWithChildren<{ renderWhileLoading: boolean }>) => {
  const { isLoading, bookingPageStatus } = useAuth();
  const siteFailure = useSiteFailure();

  // This gate renders instead of its children, so every other query unmounts
  // and none is left to succeed and clear the store. The tenant record is the
  // answer that defines the studio's state, so it is what re-probes until the
  // studio answers again — for either kind. Without it on `unreachable`, one
  // transient proxy fault holds the outage panel for the life of the tab, since
  // the survivors all sit under retry:false with no interval.
  useTenant(siteFailure ? REPROBE_MS : false);

  if (isLoading && !renderWhileLoading) {
    return <PageContainer className="flex items-center justify-center" />;
  }

  // Covers the outage too, not just a dark studio: resolveBookingPageStatus
  // folds the store's `unreachable` kind into this one answer.
  if (bookingPageStatus !== 'available') {
    return <BookingPageUnavailable status={bookingPageStatus} />;
  }

  return children;
};
